import { forwardRef } from 'react';

import { MaintenanceTableDefaultPlaceholder } from '@/components/sections/service/maintenance-list/default-placeholder';
import { MaintenanceListRemark } from '@/components/sections/service/maintenance-list/remark';
import type { MaintenanceListSearchFilterData } from '@/components/sections/service/maintenance-list/search-filter';
import { MaintenanceListSearchFilter } from '@/components/sections/service/maintenance-list/search-filter';
import type {
  MaintenanceListTableData,
  MaintenanceListTableDisplay,
} from '@/components/sections/service/maintenance-list/table';
import { MaintenanceListTable } from '@/components/sections/service/maintenance-list/table';
import type { MaintenanceListTableBriefData } from '@/components/sections/service/maintenance-list/table/brief';
import type { MaintenanceListTableFullData } from '@/components/sections/service/maintenance-list/table/full';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';

export interface MaintenanceListProps {
  title: string;
  description: string;
  filter: MaintenanceListSearchFilterData;
  defaultPlaceholder: {
    imageMobile: string;
    imageDesktop: string;
    placeholder: string;
  };
  table?: {
    data: MaintenanceListTableData;
    full?: MaintenanceListTableFullData;
    brief: MaintenanceListTableBriefData;
    headerTitle?: string;
    tableDisplay?: MaintenanceListTableDisplay;
  };
  remark?: string;
  isStartPage?: boolean;
  ref?: any;
  tableDisplay?: MaintenanceListTableDisplay;
  onTableDisplayChange?: (status: MaintenanceListTableDisplay) => void;
}

export const MaintenanceList = forwardRef(
  (
    {
      title,
      description,
      defaultPlaceholder,
      filter,
      table,
      isStartPage,
      remark,
      tableDisplay,
      onTableDisplayChange,
    }: MaintenanceListProps,
    forwardRef: any
  ) => {
    const onDisplayChange = (status: MaintenanceListTableDisplay) => {
      if (onTableDisplayChange) {
        onTableDisplayChange(status);
      }
    };
    return (
      <>
        <MaintenanceListSearchFilter
          title={title}
          description={description}
          tableDisplay={tableDisplay}
          {...filter}
        />
        <MaintenanceTableDefaultPlaceholder
          isVisible={isStartPage || table?.data.selectedIndex === -1}
          {...defaultPlaceholder}
        />
        {(table?.data.selectedIndex ?? -1) > -1 && (
          <Section
            size="2xs"
            overflow="visible"
            ref={forwardRef}
            test="section_maintenance_searchResult"
            css={{
              bc: '$gray300',
            }}
          >
            <MaintenanceListTable
              {...table}
              tableDisplay={tableDisplay}
              onTableDisplayChange={onDisplayChange}
            />
            {!!remark && (
              <Container>
                <Box marginTop="6">
                  <MaintenanceListRemark htmlContent={remark} />
                </Box>
              </Container>
            )}
          </Section>
        )}
      </>
    );
  }
);

MaintenanceList.displayName = 'SectionServiceMaitenanceList';
